// progress.ts — the user's journey state.
//
// This is the ONLY place per-user progress is read/written. Screens never touch
// storage directly; they go through useProgress(). The shape is deliberately
// transport-agnostic: `ProgressStore` could be swapped for a Supabase/Firebase
// backed implementation later with no change to the hooks or UI.

import { storage } from './storage';
import { PROGRESS_SEED } from '@/data/progress-seed';
import type { GuideHook } from '@/data/guideMessages';

export type AppRoute = 'home' | 'recovery' | 'regulate';

/** One guide-presence impression (for the local retention instrumentation). */
export interface GuideImpression {
  hook: GuideHook;
  clipId: string;
  quoteId?: string;
  at: string;
  /** H2 only — the user engaged (completed a step) in the session after this return message. */
  reengaged?: boolean;
}

/** Guide-presence persisted state: quote rotation + frequency caps + impressions. */
export interface GuideState {
  /** LRU recency of quote ids per hook (oldest → newest). */
  recentQuoteIds: Partial<Record<GuideHook, string[]>>;
  /** once → true; light → the dayKey it last showed; others → true. */
  seen: Partial<Record<GuideHook, string | boolean>>;
  /** ACT boundaries already shown (H4 perBoundary). */
  boundariesShown: string[];
  /** H2 — the dayKey of the last qualifying return we handled. */
  lastReturnHandled: string | null;
  lastGuideMessageAt: string | null;
  impressions: GuideImpression[];
}

/** Which programme track the user is following: the main recovery programme, or
 *  the gentler audio-led self-compassion "soft landing" route. */
export type ProgrammeRoute = 'main' | 'compassion';

/** The daily capacity check-in answer — capacity/need, never symptom severity. */
export type DayCapacity = 'steady' | 'harder' | 'hard';

/** A Gollwitzer if-then implementation intention captured after a practice. */
export interface Intention {
  situation: string;
  action: string;
  createdAt: string;
}

/** A first-class, no-shame pause of the programme. */
export interface PausedState {
  since: string;
  reason: string;
}

/** A user-saved reflection answer (kept on-device). */
export interface SavedReflection {
  stepId: string;
  module: string;
  prompt: string;
  body: string;
  /** ISO timestamp of when it was kept. */
  savedAt: string;
  /** True when this came from an "aha moment" capture — rendered in gold. */
  aha?: boolean;
}

export interface ProgressState {
  version: number;
  completedSteps: string[];
  completedModules: string[];
  /** The step the user is currently on (resume target). */
  currentStepId: string | null;
  /** Which route the user last opened. */
  lastRoute: AppRoute;
  /** Resume positions in seconds, keyed by stepId (video/audio). */
  positions: Record<string, number>;
  /** Reflection answers keyed by stepId. */
  reflections: Record<string, SavedReflection>;
  /** Favourited step ids, in the order they were added (newest last). */
  favourites: string[];
  /** Today's arriving mood id, if checked in. */
  mood: string | null;
  /** Ids of testimonial cards the user has dismissed (tapped away). */
  dismissedTestimonials: string[];
  /** Developer mode — turns off the pacing prompts so content can be reviewed freely. */
  devMode: boolean;
  /** App-wide reading text scale (1 = default). Set from the transcript font control. */
  fontScale: number;
  /** ISO timestamp each step was first completed. */
  completedAt: Record<string, string>;
  /** A CARE reflection is being offered (after a Settle-My-Emotions session). */
  careTriggered: boolean;
  /** Empowering actions the user committed to in CARE — soft reminders on Today. */
  careActions: CareAction[];
  /** Whether the user journals in the app. Default false = use their own physical
   *  journal (recommended). When false, reflection surfaces invite off-app writing
   *  and store nothing. */
  inAppJournaling: boolean;
  /** Whether the user has made the journaling choice yet (gates the first-run note). */
  journalingChoiceMade: boolean;
  /** Which programme track is active — main recovery, or the self-compassion
   *  soft-landing route. Toggled from the programme section. */
  activeProgramme: ProgrammeRoute;
  /** Whether the user has worked through the onboarding tiles (journaling choice,
   *  sign-in, …). The app is gated behind these until done. */
  onboardingDone: boolean;
  /** First-session onboarding instrumentation: furthest step reached + when finished. */
  onboarding: { reached: number; completedAt: string | null };
  /** The single canonical daily-anchor time ("HH:MM"), or null = off. The cue the
   *  guide hooks read AND the time the main daily reminder fires. */
  morningAnchorTime: string | null;
  /** Optional daily Calm practice reminder time ("HH:MM"), or null = off. */
  calmPracticeTime: string | null;
  /** UNUSED: retired in favour of morningAnchorTime (migrated on load). Kept in the
   *  type for storage/sync compatibility; always null after load. */
  programmeReminder: { time: string; everyDays: number } | null;
  /** Today-screen reminder nudge tile: true once the user dismisses it. Reminders
   *  are no longer set in onboarding — this Craig-fronted tile invites them, and
   *  can be hidden/reshown from You → Reminders. */
  reminderNudgeDismissed: boolean;
  /** ISO time the one-time "Your free trial has ended" sheet was first shown (TRIAL_FULL_LOCK), or null. */
  trialEndedSeenAt: string | null;
  /** True once the participant's FIRST wellbeing check-in has been opened, so the
   *  one-time demographics survey ("who are we supporting?") is requested exactly
   *  once. Drives the demographics=1 flag on the check-in URL. */
  demographicsCheckinDone: boolean;
  /** Guide presence state (rotation, frequency caps, impressions). */
  guide: GuideState;
  // ── behaviour-change layer (anchor, presence, practice, pause, reflection) ──
  /** Today's capacity check-in, keyed by local date (YYYY-MM-DD). Routes the day. */
  dailyCapacity: Record<string, DayCapacity>;
  /** Distinct local dates the user showed up — presence, never a streak. */
  presenceDays: string[];
  /** Steps the user actively practised/tried (distinct from passively seen) —
   *  a reflection dwelt on for at least the practised bar counts here. */
  practisedSteps: string[];
  /** If-then implementation intentions, keyed by step id. */
  intentions: Record<string, Intention>;
  /** Anti-skip memory: the last few reflections, each flagged true if advanced
   *  under the skip floor. Drives escalation ("3 of the last 5"). Silent signal. */
  reflectionSkips: boolean[];
  /** The user said "I'm doing these my own way" — durably silences reflection nudges. */
  reflectionOwnWay: boolean;
  /** One-shot: a Key Skill chapter was just completed — Home shows the H5 mastery
   *  guide once, then clears this. Persisted so it survives the home remount that a
   *  chapter completion triggers (and is consumed exactly once). */
  guideMasteryDue: boolean;
  /** One-shot: the reflection-skip run just crossed into "escalated" (over-pacing)
   *  — Home shows the H7 over-pacing guide once, then clears this. */
  guideOverPacingDue: boolean;
  /** A first-class pause of the programme, or null when active. */
  paused: PausedState | null;
  /** Act-boundary processing screens already shown (e.g. 'act1'), so each appears once. */
  actBoundariesSeen: string[];
  /** Clinician Review Mode: when on, content surfaces show a comment affordance.
   *  Off by default and for all end users — purely an authoring aid. */
  reviewMode: boolean;
  /** Review notes left on content, newest last. Local-only; exported as markdown. */
  reviewComments: ReviewComment[];
  /** Toolkit (Your stuff): tool ids whose "new tool unlocked" guide beat has been
   *  shown, so each unlock is announced exactly once. */
  toolkitAnnounced: string[];
  /** Whether the one-time "explore your toolkit" encouragement beat has shown. */
  toolkitIntroSeen: boolean;
  /** Whether the one-time "Inspire me" intro tile (module 1) has been dismissed. */
  inspireIntroSeen: boolean;
  /** A just-finished chapter to celebrate as a tile at the top of the Today feed.
   *  Persisted (not derived) so it survives the home remount a completion triggers,
   *  and shows for every completion path. Cleared when the user taps it away. */
  chapterCelebration: { moduleId: string; next: string } | null;
  /** Everyday-encouragement (H11) cadence: a running session count, the session it
   *  last fired on, and the day it last fired (to keep it every-N-sessions and never
   *  on consecutive days). */
  encouragement: { sessions: number; lastFiredSession: number; lastDay: string | null };
  /** Check-in cadence (H6): the session index (encouragement.sessions) at the user's
   *  last Calm-Your-Body check-in. null = never. H6 invites a check-in when the user
   *  is 3+ sessions past this. */
  lastCheckinSession: number | null;
  /** ISO time the participant last OPENED a longitudinal research wellbeing check-in
   *  (ONS-4 + WSAS + 6-item). Anchors the time-based check-in schedule (baseline →
   *  6 → 12 → 18 weeks → every 2 months from enrolment). null = never. */
  lastResearchCheckinAt: string | null;
  /** Legacy: the schedule window-day a dismiss was tied to (per-window suppression).
   *  Superseded by checkinSnoozeUntil; retained only so old saved state parses. */
  checkinSnoozeWindow: number | null;
  /** ISO time until which the check-in prompt is snoozed. Set to now + REPROMPT_DAYS
   *  whenever the prompt is opened or dismissed, so an unfinished/skipped check-in
   *  re-surfaces every few days until it is recorded on the backend. null = not snoozed. */
  checkinSnoozeUntil: string | null;
  /** Backend-verified count of recorded check-in waves (from fetchMyProgress). The
   *  source of truth for "done" — opening a check-in never increments this; only a
   *  recorded submission does. Cached so Today can gate the prompt without a blocking
   *  fetch; refreshed fail-soft on load.
   *
   *  NULL MEANS UNKNOWN, not zero: the server has never answered on this device.
   *  The prompt stays silent until it has, because a fresh install cannot tell a
   *  genuinely new participant from someone mid-programme whose backend is down. */
  completedCheckinCount: number | null;
  /** Multi-day held SC steps: stepId → distinct day-keys practised. A step whose
   *  label spans N days must appear here on N distinct days before it completes. */
  practiceDays: Record<string, string[]>;
  // ── research data layer (consent-gated; see data.js backend) ──────────────
  /** Who the account-holder is, chosen once in onboarding (and written to
   *  profiles.user_type, locked). ONLY 'survivor' is offered the research consent
   *  step, given a PID, or prompted for check-ins — everyone else gets the full app
   *  but never enters the research/outcome data. null = not yet asked. */
  userType: 'survivor' | 'curious' | 'staff' | 'other' | null;
  /** One-time research opt-in. 'unset' = not yet asked; 'granted'/'declined' final.
   *  Surveys + lesson feedback are only collected/synced when 'granted'. Progress
   *  sync is operational and runs regardless (low-sensitivity, pseudonymised). */
  researchConsent: 'unset' | 'granted' | 'declined';
  /** The research-statement version the current researchConsent decision was made
   *  against (null if never decided). Lets a materially changed statement re-ask. */
  researchConsentVersion: string | null;
  /** Consent ledger (local mirror of the Supabase `consents` log; the source of truth
   *  is the server). The accepted Terms & Privacy version, or null if not yet accepted.
   *  The consent gate re-appears whenever this is below CONSENT_VERSIONS.terms. */
  termsAcceptedVersion: string | null;
  /** Chapter (module) ids whose end-of-chapter "How was this?" card has been answered
   *  OR skipped — so it's asked at most once per chapter (NOT per step/lesson). */
  chapterFeedbackGiven: string[];
  /** Act ids whose end-of-act wellbeing survey has been answered OR skipped. */
  actSurveysGiven: string[];
  // ── self-soothing engagement counts (for the "Your progress" view + logging) ──
  /** Cumulative count of Calm Your Body sessions started. A gentle "moments of
   *  practice" number, never a streak; only ever grows. */
  calmSessionsCount: number;
  /** Cumulative count of Toolkit tools opened/used. */
  toolUsesCount: number;
  // ── FOGS full check ─────────────────────────────────────────────────────────
  /** Themes the onboarding quick check surfaced (reveal keys, incl. 'ex'). Themes only,
   *  never answers. null = quick check not seen. */
  needsThemes: { themes: string[]; at: string } | null;
  /** Result of the full check: ONLY the flagged section keys travel back, never answers. */
  fogsResult: { flagged: string[]; at: string } | null;
  /** ISO time the full check was last opened from the app. */
  fogsOpenedAt: string | null;
  /** How that open went: 'linked' (PID-saved, result comes back), 'unlinked' (native, nothing
   *  comes back), or 'web' (the page returns by URL). Drives the "Done it?" fallback. */
  fogsOpenedMode: 'linked' | 'unlinked' | 'web' | null;
  /** Unused: kept only for sync/storage compatibility; no longer drives any UI. */
  /** The "How did your past relationship affect you?" card was dismissed before Act I ended. */
  fogsDismissedBeforeAct1: boolean;
  /** Unused: kept only for sync/storage compatibility; no longer drives any UI. */
  /** The one Act-I-complete reminder has been dismissed: no further prompts, ever. */
  fogsActReminderDone: boolean;
  /** "Done it?" fallback: the user marked the check done themselves (no result came back). */
  fogsDoneLocal: boolean;
}

/** A clinician review note left on a piece of content "as presented" (Review Mode).
 *  Local-only, never shown to end users; keyed by the content it annotates so the
 *  export is directly actionable against the content manifest. */
export interface ReviewComment {
  id: string;
  /** The annotated content: a step id (kind 'step') or a module id (kind 'chapter'). */
  targetId: string;
  kind: 'step' | 'chapter';
  text: string;
  createdAt: string;
  updatedAt: string;
}

/** An empowering thing the user said they'd do (CARE step E) — a gentle reminder. */
export interface CareAction {
  id: string;
  text: string;
  createdAt: string;
  /** ISO when the user confirmed they did it, else null. */
  doneAt: string | null;
}

// v2: real programme content (fresh start). Bumping the key discards old demo state.
const KEY = 'renurture.progress.v2';

function clone(state: ProgressState): ProgressState {
  return JSON.parse(JSON.stringify(state));
}

export const ProgressStore = {
  load(): ProgressState {
    const raw = storage.get(KEY);
    if (raw == null) {
      const seed = clone(PROGRESS_SEED);
      storage.setJSON(KEY, seed);
      return seed;
    }
    try {
      const parsed = JSON.parse(raw) as ProgressState;
      // Forward-compatible defaults for any missing fields.
      return {
        ...clone(PROGRESS_SEED),
        ...parsed,
        positions: { ...parsed.positions },
        reflections: { ...parsed.reflections },
        favourites: parsed.favourites ?? [],
        dismissedTestimonials: parsed.dismissedTestimonials ?? [],
        devMode: parsed.devMode ?? false,
        fontScale: parsed.fontScale ?? 1,
        completedAt: { ...parsed.completedAt },
        careTriggered: parsed.careTriggered ?? false,
        careActions: parsed.careActions ?? [],
        inAppJournaling: parsed.inAppJournaling ?? false,
        journalingChoiceMade: parsed.journalingChoiceMade ?? false,
        activeProgramme: parsed.activeProgramme ?? 'main',
        onboardingDone: parsed.onboardingDone ?? false,
        onboarding: {
          reached: parsed.onboarding?.reached ?? 0,
          completedAt: parsed.onboarding?.completedAt ?? null,
        },
        // Migration: carry a pre-1A `calmReminder.time` into the canonical anchor so
        // existing users don't lose their Calm Your Body reminder.
        // Migration: the retired programme reminder's time (if a valid HH:MM) becomes the
        // main daily reminder when none is set; the field is then nulled.
        morningAnchorTime:
          parsed.morningAnchorTime ??
          (parsed as { calmReminder?: { time?: string } }).calmReminder?.time ??
          (/^\d{1,2}:\d{2}$/.test(parsed.programmeReminder?.time ?? '') ? parsed.programmeReminder!.time : null) ??
          null,
        calmPracticeTime: parsed.calmPracticeTime ?? null,
        programmeReminder: null,
        reminderNudgeDismissed: parsed.reminderNudgeDismissed ?? false,
        trialEndedSeenAt: parsed.trialEndedSeenAt ?? null,
        demographicsCheckinDone: parsed.demographicsCheckinDone ?? false,
        guide: {
          recentQuoteIds: { ...(parsed.guide?.recentQuoteIds ?? {}) },
          seen: { ...(parsed.guide?.seen ?? {}) },
          boundariesShown: parsed.guide?.boundariesShown ?? [],
          lastReturnHandled: parsed.guide?.lastReturnHandled ?? null,
          lastGuideMessageAt: parsed.guide?.lastGuideMessageAt ?? null,
          impressions: parsed.guide?.impressions ?? [],
        },
        dailyCapacity: { ...parsed.dailyCapacity },
        presenceDays: parsed.presenceDays ?? [],
        practisedSteps: parsed.practisedSteps ?? [],
        intentions: { ...parsed.intentions },
        paused: parsed.paused ?? null,
        actBoundariesSeen: parsed.actBoundariesSeen ?? [],
        reflectionSkips: parsed.reflectionSkips ?? [],
        reflectionOwnWay: parsed.reflectionOwnWay ?? false,
        guideMasteryDue: parsed.guideMasteryDue ?? false,
        guideOverPacingDue: parsed.guideOverPacingDue ?? false,
        reviewMode: parsed.reviewMode ?? false,
        reviewComments: parsed.reviewComments ?? [],
        toolkitAnnounced: parsed.toolkitAnnounced ?? [],
        toolkitIntroSeen: parsed.toolkitIntroSeen ?? false,
        inspireIntroSeen: parsed.inspireIntroSeen ?? false,
        chapterCelebration: parsed.chapterCelebration ?? null,
        encouragement: parsed.encouragement ?? { sessions: 0, lastFiredSession: 0, lastDay: null },
        lastCheckinSession: parsed.lastCheckinSession ?? null,
        lastResearchCheckinAt: parsed.lastResearchCheckinAt ?? null,
        checkinSnoozeWindow: parsed.checkinSnoozeWindow ?? null,
        checkinSnoozeUntil: parsed.checkinSnoozeUntil ?? null,
        // ?? null, not ?? 0 — an absent field means this device has never had an
        // answer from the server, which is not the same as "no waves recorded".
        completedCheckinCount: parsed.completedCheckinCount ?? null,
        practiceDays: parsed.practiceDays ?? {},
        userType: parsed.userType ?? null,
        researchConsent: parsed.researchConsent ?? 'unset',
        researchConsentVersion: parsed.researchConsentVersion ?? null,
        termsAcceptedVersion: parsed.termsAcceptedVersion ?? null,
        chapterFeedbackGiven: parsed.chapterFeedbackGiven ?? [],
        actSurveysGiven: parsed.actSurveysGiven ?? [],
        calmSessionsCount: parsed.calmSessionsCount ?? 0,
        toolUsesCount: parsed.toolUsesCount ?? 0,
        needsThemes: parsed.needsThemes ?? null,
        fogsResult: parsed.fogsResult ?? null,
        fogsOpenedAt: parsed.fogsOpenedAt ?? null,
        fogsOpenedMode: parsed.fogsOpenedMode ?? null,
        fogsDismissedBeforeAct1: parsed.fogsDismissedBeforeAct1 ?? false,
        fogsActReminderDone: parsed.fogsActReminderDone ?? false,
        fogsDoneLocal: parsed.fogsDoneLocal ?? false,
      };
    } catch {
      return clone(PROGRESS_SEED);
    }
  },

  save(state: ProgressState): void {
    storage.setJSON(KEY, state);
  },

  /** Reset to the initial seed (used by Settings → clear progress). */
  reset(): ProgressState {
    const seed = clone(PROGRESS_SEED);
    storage.setJSON(KEY, seed);
    return seed;
  },
};
